import { Injectable } from '@angular/core';
import { ActivatedRoute, Router } from '@angular/router';
import { BehaviorSubject, combineLatest, first, firstValueFrom, map, Observable } from 'rxjs';
import { AppService } from 'src/app/app.service';
import { ResourceApi } from 'src/app/shared/apis/resource.api';
import { TokenAddressApi } from 'src/app/shared/apis/tokenaddress.api';
import { WithdrawApi } from 'src/app/shared/apis/withdraw.api';
import { BankCardApi } from 'src/app/shared/apis/bank-card.api';
import { ICSOption } from 'src/app/shared/components/cs-select/cs-select.component';
import { DepositMode } from 'src/app/shared/interfaces/account.interface';
import { VerifyAction } from 'src/app/shared/interfaces/auth.interface';
import { DepositAddressInterface } from 'src/app/shared/interfaces/deposit.interface';
import { CurrenciesInterface, PaymentListResponse } from 'src/app/shared/interfaces/deposit.interface';
import { AddBankCardPost, BankCard } from 'src/app/shared/interfaces/bank-card.interface';
import { Quotalimit } from 'src/app/shared/interfaces/withdraw.interface';
import { ResponseData } from 'src/app/shared/interfaces/response.interface';
import { AddAddressParam, GetAddParam } from 'src/app/shared/interfaces/tokenaddress.interface';
import { CoinWithdrawReq } from 'src/app/shared/interfaces/withdraw.interface';
import {
  MatConnectedDialog,
  MatConnectedDialogMobileAnimation,
  MatConnectedDialogRef,
} from 'src/app/shared/service/connected-dialog';
import { DataCollectionService } from 'src/app/shared/service/data-collection.service';
import { GeneralService } from 'src/app/shared/service/general.service';
import { LocaleService } from 'src/app/shared/service/locale.service';
import { LocalStorageService } from 'src/app/shared/service/localstorage.service';
import { ToastService } from 'src/app/shared/service/toast.service';
import { TokenNetworksValidationService } from 'src/app/shared/service/token-networks-validation';
import { UserAssetsService } from '../user-asset/user-assets.service';
import { WalletDialog } from 'src/app/shared/service/dialog-loaders';
import { LazyDialogService } from 'src/app/shared/service/lazy-dialog.service';
// 只作型別使用：WalletDialogComponent 會連鎖靜態 import
// FirstDepositComponent / DepositWithdrawComponent，兩者都牽進 Web3WalletService
// （ethers / viem / TonConnect / AppKit）。改為經 LazyDialogService + dialog-loaders 動態載入。
import type { WalletDialogComponent } from './wallet-dialog';
import type { BankDetailsRoute } from './deposit-withdraw/bank-details-manager/add-bank-details.component';

export enum WalletDialogType {
  FIRST_DEPOSIT = 'first_deposit',
  DEPOSIT_WITHDRAW = 'deposit_withdraw',
  ADDRESS_QR = 'address_qr',
  SAVED_ADDRESS = 'saved_address',
  ADD_EDIT_ADDRESS = 'add_edit_address',
  VERIFY_2FA = 'verify-2fa',
  DEPOSIT_TRANSACTION_DETAILS = 'deposit_Transaction_details',
  CASH_DEPOSIT_CONFIRMATION = 'cash_deposit_confirmation',
  SAVED_PAYMENT_DETAILS = 'saved_payment_details',
  WITHDRAW_TRANSACTION_DETAILS = 'withdraw_Transaction_details',
  CONFIRM_PAGE = 'confirm_page',
}
/**
 * deposit transfer 暫存資料
 * 因為更換頁面組件會銷毀，所以從deposit/transfer跳到qrCode頁面時需要暫存資料
 */
export interface DepositPreservedState {
  currentCurrency: string;
  cryptoList: ICSOption<string>[];
  selectedNetwork: string;
  networkList: { value: string; label?: string; network?: string }[];
  depositAddress: DepositAddressInterface;
  selectedNetworkSchemaPrefix: string;
}
/**
 * withdraw transfer 暫存資料
 * 因為更換頁面組件會銷毀，所以從deposit/transfer跳到qrCode頁面時需要暫存資料
 */
export interface WithdrawPreservedState {
  currentCurrency: string;
  selectedNetwork: string;
  address?: string;
  addressId?: string;
  amount: number;
}
/** 新增地址 */
export interface AddAddressData {
  remark: string;
  network: string;
  address: string;
}
/** Cash withdrawal state survives Wallet dialog content changes. */
export interface CashWithdrawPreservedState {
  currency: CurrenciesInterface | null;
  cards: BankCard[];
  paymentMethods: PaymentListResponse[];
  limit: Quotalimit | null;
  minimumAmount: number;
  maximumAmount: number;
  paymentMethod: string;
  bankCardId: string;
  amount: number | null;
  route: BankDetailsRoute | null;
}

export interface BankDetailsContext {
  currency: string;
  paymentMethod?: string;
  route: BankDetailsRoute;
}

/** Bank-card fields retained while the player completes the in-modal OTP step. */
export interface BankCardDraft {
  currencyType: string;
  name: string;
  bankCode: string;
  bankName?: string;
  cardNum: string;
  swiftBic?: string;
  iban?: string;
  paymentMethod?: string;
  branchName?: string;
}
/** 存提款成功詳情 */
export interface WalletDetailsData {
  header?: string;
  typeTitle?: 'Deposit' | 'Withdrawal' | 'BuyCrypto';
  status: 'success' | 'fail';
  title: string;
  content: string;
  data?: any;
  orderStatus?: 'pending' | 'fail' | 'success';
}

/**
 * PIQ may redirect the player back to a freshly loaded application. Keep the
 * confirmation data independently of the embedded cashier so Cash can render
 * its final request-created modal after that redirect.
 */
export interface CashDepositConfirmationData {
  orderId: string;
  amount: number | string;
  currency: string;
}

const CASH_DEPOSIT_CONFIRMATION_STORAGE_KEY = 'cash-deposit-confirmation';
/** 存提款失敗的confirm page */
export interface ConfirmPageData {
  header: string;
  title: string;
  message: string;
}

/**
 * 獲取虛擬貨幣列表與當前選擇幣種的結果
 */
export interface CurrencyInfoResult {
  cryptoList: ICSOption<string>[];
  currentCurrency: string;
}

@Injectable({ providedIn: 'root' })
export class WalletService {
  constructor(
    private router: Router,
    private route: ActivatedRoute,
    public matConnectedDialog: MatConnectedDialog,
    private lazyDialog: LazyDialogService,
    private userAssetsService: UserAssetsService,
    private generalService: GeneralService,
    private toast: ToastService,
    private tokenNetworksValidationService: TokenNetworksValidationService,
    private appService: AppService,
    private withdrawApi: WithdrawApi,
    private bankCardApi: BankCardApi,
    private localeService: LocaleService,
    private tokenAddressApi: TokenAddressApi,
    private resourceApi: ResourceApi,
    private dataCollectionService: DataCollectionService,
    private localStorageService: LocalStorageService,
  ) {}
  private dialogRef?: MatConnectedDialogRef<WalletDialogComponent>;
  public walletDialogType$: BehaviorSubject<WalletDialogType> = new BehaviorSubject<WalletDialogType>(
    WalletDialogType.FIRST_DEPOSIT,
  );

  /** deposit transfer 暫存資料集合 */
  preservedDepositState: DepositPreservedState | null = null;
  /** withdraw transfer 暫存資料集合 */
  preservedWithdrawState: WithdrawPreservedState | null = null;
  /** 新增地址暫存參數 */
  addAddressData: AddAddressData | null = null;
  /** 刪除地址暫存參數 刪除地址只需要addressId */
  addressId: number | null = null;
  /** withdraw地址輸入的模式是否為手動 預設為手動輸入 */
  useManualEnter: boolean = true;
  /** State/data used by the Cash bank-details pages within the existing Wallet modal. */
  cashWithdrawState: CashWithdrawPreservedState | null = null;
  bankDetailsContext: BankDetailsContext | null = null;
  bankCardDraft: BankCardDraft | null = null;
  bankDetailsNeedRefresh = false;

  otpType: VerifyAction = 'Withdraw';
  email: string = '';

  /** 訂單完成的詳情 */
  walletDetailsData: WalletDetailsData | null = null;

  storeCashDepositConfirmation(data: CashDepositConfirmationData): void {
    try {
      sessionStorage.setItem(CASH_DEPOSIT_CONFIRMATION_STORAGE_KEY, JSON.stringify(data));
    } catch (error) {
      // The redirect URL also carries amount/currency. Storage is a recovery
      // path for the PIQ order ID when the embedded cashier reloads the app.
      console.error('Unable to persist Cash deposit confirmation data', error);
    }
  }

  consumeCashDepositConfirmation(): CashDepositConfirmationData | null {
    try {
      const raw = sessionStorage.getItem(CASH_DEPOSIT_CONFIRMATION_STORAGE_KEY);
      sessionStorage.removeItem(CASH_DEPOSIT_CONFIRMATION_STORAGE_KEY);
      if (!raw) return null;

      const data = JSON.parse(raw) as Partial<CashDepositConfirmationData>;
      if (!data.orderId || data.amount === undefined || !data.currency) return null;
      return { orderId: String(data.orderId), amount: data.amount, currency: String(data.currency) };
    } catch (error) {
      console.error('Unable to restore Cash deposit confirmation data', error);
      return null;
    }
  }

  showCashDepositConfirmation(data: CashDepositConfirmationData): void {
    this.walletDetailsData = {
      header: 'Wallet',
      status: 'success',
      title: 'Deposit is being processed',
      content: 'You can track its progress in transaction page',
      typeTitle: 'Deposit',
      data: {
        txId: data.orderId,
        amount: data.amount,
        currency: data.currency,
      },
      orderStatus: 'pending',
    };
    this.setWalletDialogType(WalletDialogType.CASH_DEPOSIT_CONFIRMATION);
  }
  /** confirm page的資料 */
  confirmPageData: ConfirmPageData | null = null;
  /** 所有網路資料 */
  allNetworkList: any[] = [];
  /** 是否已有入金紀錄 */
  private hasDepositRecord: boolean | null = null;
  /** 入金紀錄查詢中的 promise，避免重複請求 */
  private depositCheckTask: Promise<boolean> | null = null;

  targetCurrency: string = '';
  bonusId: string = '';

  /** 獲取到暫存資料之後要清除，以免影響其他資料   */
  clearDepositState(): void {
    this.preservedDepositState = null;
  }

  clearCashWithdrawState(): void {
    this.cashWithdrawState = null;
    this.bankDetailsContext = null;
    this.bankCardDraft = null;
    this.bankDetailsNeedRefresh = false;
  }

  /**
   * 監聽談窗是否被開啟
   *
   * @returns
   */
  isDialogOpen(): boolean {
    return !!this.dialogRef;
  }

  /**
   * 用網址餐數打開彈窗
   *
   * @param type
   * @param dialog
   * @param tab
   * @param subTab
   * @param sub
   * @param targetCurrency
   * @param id
   * @param bonusId
   */
  openWalletDialogByUrl(dialog?: string, tab?: string, subTab?: string, targetCurrency?: string, bonusId?: string) {
    this.router.navigate([], {
      relativeTo: this.route,
      queryParams: {
        tab,
        subTab,
        dialog, // 👈 改成加在 queryParams 裡
        targetCurrency, // 目標幣種
        bonusId, //紅利id
      },
      queryParamsHandling: 'merge', // 🔄 避免清掉其他現有 query（可選）
    });
  }

  /**
   * 用在app.component.ts偵測到特定網址餐數打開彈窗
   *
   * @param type
   */
  async openWallet(type: WalletDialogType = WalletDialogType.FIRST_DEPOSIT): Promise<void> {
    if (!this.dialogRef) {
      this.walletDialogType$.next(type);

      const dialogRef = await this.lazyDialog.open(WalletDialog, {
        mobileAnimation: MatConnectedDialogMobileAnimation.SLIDE_UP,
        mobileFixedBottom: true,
        closeOnNavigation: false,
      });

      // chunk 載入失敗，或載入期間已有另一次開啟
      if (!dialogRef) return;
      if (this.dialogRef) {
        dialogRef.close();
        return;
      }

      this.dialogRef = dialogRef;
      this.dialogRef.afterClosed().subscribe(() => {
        this.dialogRef = undefined;
        this.walletDialogType$.next(WalletDialogType.FIRST_DEPOSIT);
        this.clearCashWithdrawState();
        this.clearUrlParams();
      });
    }
  }

  /**
   * 關閉彈窗
   */
  closeWallet(): void {
    this.dialogRef?.close();
    this.dialogRef = undefined;
    this.clearUrlParams();

    // 回到初始狀態就是first deposit那頁
    this.walletDialogType$.next(WalletDialogType.FIRST_DEPOSIT);
    this.useManualEnter = true; // 重置手動輸入狀態
    this.clearCashWithdrawState();
  }

  /**
   * 封裝queryParams清除
   */
  clearUrlParams() {
    this.router.navigate([], {
      queryParams: {},
      fragment: undefined,
      relativeTo: this.route,
    });
  }

  /**
   * 驗證路徑是否合法
   *
   * @param tab
   * @param sub
   * @returns
   */
  isValidTabPair(tab?: string, sub?: string): boolean {
    const validTabs = ['deposit', 'withdraw'];
    const validSubs = ['web3Wallet', 'transfer', 'buyCrypto', 'cash'];
    return validTabs.includes(tab!) && validSubs.includes(sub!);
  }

  /**
   * 跳轉去錢包tab的路由
   *
   * @param tab
   * @param subTab
   */
  jump(tab: string, subTab: string) {
    this.walletDialogType$.next(WalletDialogType.DEPOSIT_WITHDRAW);
    this.router.navigate([], {
      queryParams: {
        tab,
        subTab,
        dialog: 'wallet', // ✅ 改成 query param
      },
      queryParamsHandling: 'merge', // 保留其他 query 參數
    });
  }

  /**
   * 跳轉到不同的錢包類型
   */

  setWalletDialogType(type: WalletDialogType) {
    this.walletDialogType$.next(type);
  }

  /**
   * 獲取虛擬貨幣列表與當前選擇幣種
   *
   * @abstract
   */
  getCryptoList(): Observable<CurrencyInfoResult> {
    return combineLatest([
      this.appService.currencies$.pipe(
        // 過濾掉非虛擬貨幣和不可見的幣種
        map(item => item.filter(x => x.isDigital && x.isVisible)),
        first(item => !!(item.length > 0)),
      ),
      this.appService.currentCurrency$.pipe(first(v => !!v)),
    ]).pipe(
      map(([cryptoList, currentCurrency]) => {
        const formattedList: ICSOption<string>[] = cryptoList.map(item => {
          return {
            label: item.name,
            value: item.currency,
            img: item.icon,
          };
        });

        // 如果當前幣種不是虛擬貨幣，則默認使用USDT
        const currency = currentCurrency.isDigital ? currentCurrency.currency : 'USDT';

        return {
          cryptoList: formattedList,
          currentCurrency: currency,
        };
      }),
    );
  }

  /**
   * 獲取網路存提款支持得網路
   *
   * @param category
   * @param currency
   */
  async getNetwork(category: 'Deposit' | 'Withdraw', currency: string) {
    await this.userAssetsService.getWithdrawTokenNetWorks(category);
    const temp = this.tokenNetworksValidationService.getCryptoDepositNetWorkNuValiedList(currency || 'USDT');
    return temp.map(item => {
      const base = {
        label: item.networkInfo.desc,
        value: item.networkInfo.network,
      };
      if (category === 'Withdraw') {
        return {
          ...base,
          networkInfo: item.networkInfo,
        };
      }
      return base;
    });
  }

  /**
   * 獲取所有網路，用於認證網路或地址
   *
   * @returns
   */
  async getAllNetworks(): Promise<void> {
    try {
      const result = await this.userAssetsService.getAllNetWorksInfor();
      this.allNetworkList = Array.isArray(result) ? result : [];
    } catch (error) {
      this.allNetworkList = [];
      console.error('Failed to fetch network info:', error);
    }
  }

  /**
   * copy for wallet
   *
   * @param copyContent
   * @param message
   * @param type
   */
  copy(copyContent: string | null, message: string, type: 'success' | 'fail' | 'warning' = 'success'): void {
    if (copyContent) {
      this.generalService.copyText(copyContent);
      this.toast.show({
        message,
        type,
      });
    }
  }

  /**
   *
   * 獲取提款當前幣種限額等資訊
   *
   * @memberof WalletService
   */

  async getCoinLimit(currentCurrency: string) {
    const result = await firstValueFrom(this.withdrawApi.getCoinLimit(currentCurrency));
    if (result?.success) return result.data;
    return undefined;
  }

  /**
   * 2fa認證
   *
   * @param emailCode
   * @param verify
   * @returns
   */
  async verify2fa(verify: any): Promise<boolean> {
    if (verify?.success) {
      switch (this.otpType) {
        case 'Withdraw':
          await this.cryptoWithdraw(verify.data);
          break;
        case 'AddTokenAddress':
          await this.addTokenAddress(verify.data);
          break;
        case 'AddBankCard':
          return this.addBankCard(verify.data);
        case 'DeleteTokenAddress':
          await this.deleteAddress(verify.data);
          break;
        default:
          break;
      }
      return true;
    } else {
      this.onProcessError(verify);
      return false;
    }
  }

  /**
   * 接口返回 错误统一处理
   *
   * @param responseData
   * @param description reg_tip01 (该帐户已经被注册) sign_up_email (该邮箱已被注册，请重试)
   */
  onProcessError(responseData: ResponseData<string>) {
    this.toast.show({
      message: responseData?.message ? responseData.message : this.localeService.getValue('reg_f'),
      type: 'fail',
    });
  }

  /**
   * 虛擬貨幣提款
   *
   * @param key
   */
  async cryptoWithdraw(key: string) {
    if (!this.preservedWithdrawState) {
      throw new Error('No preserved withdraw state');
    }
    const param: CoinWithdrawReq = {
      key: key,
      currency: this.preservedWithdrawState.currentCurrency,
      address: this.preservedWithdrawState.address,
      addressId: this.preservedWithdrawState.addressId,
      network: this.preservedWithdrawState.selectedNetwork,
      amount: this.preservedWithdrawState.amount,
    };
    const result = await firstValueFrom(this.withdrawApi.coinWithdraw(param));
    if (result.success) {
      this.walletDetailsData = {
        status: 'success',
        title: 'Your request has been created',
        content: 'You can track its progress in the transactions page',
        typeTitle: 'Withdrawal',
        data: result.data,
        orderStatus: 'pending',
      };
      // 提款成功之後手動刷新餘額，防止ws斷線導致餘額不更新
      const userBalance = await this.userAssetsService.getUserbalance();
      this.appService.userBalance$.next(userBalance);
      this.setWalletDialogType(WalletDialogType.WITHDRAW_TRANSACTION_DETAILS);
    } else {
      switch (result?.code) {
        case '2098':
          // 禁止提款
          // TODO: 要用新的modal開啟
          this.appService.showForbidTip('income', result.message);
          break;
        case '2050':
          //TODO: 需要重新設計kyc的流程
          // this.kycDialogService.showKycError(2050, 1, false);
          console.log('kyc');
          break;
        default:
          this.confirmPageData = {
            header: 'Withdrawal',
            title: 'Withdrawal Locked',
            message: result.message,
          };
          this.setWalletDialogType(WalletDialogType.CONFIRM_PAGE);
          break;
      }
    }
  }

  /**
   * 新增地址
   *
   * @param key
   */
  async addTokenAddress(key: string) {
    const params: AddAddressParam = {
      remark: this.addAddressData?.remark || '',
      token: '',
      network: this.addAddressData?.network || '',
      address: this.addAddressData?.address || '',
      key: key,
      isWhiteList: false,
    };
    const res = await firstValueFrom(this.tokenAddressApi.addTokenaddress(params));
    if (res?.errors?.Remark && res.errors.Remark.length > 0) {
      const errorString = res.errors.Remark.join(', ');
      this.toast.show({
        message: errorString,
        type: 'fail',
      });
    } else {
      this.toast.show({
        message: res?.data ? this.localeService.getValue('add_addr_s') : res?.message || '',
        type: res?.data ? 'success' : 'fail',
      });
    }
    this.setWalletDialogType(WalletDialogType.SAVED_ADDRESS);
  }

  /** Save the bank-detail draft after Wallet's in-place 2FA screen succeeds. */
  private async addBankCard(key: string): Promise<boolean> {
    const draft = this.bankCardDraft;
    if (!draft || !key) return false;
    try {
      const request: AddBankCardPost = { ...draft, key };
      const result = await firstValueFrom(this.bankCardApi.addBankCard(request));
      if (!result?.success) {
        this.toast.show({ type: 'fail', message: result?.message || 'Unable to save payment details.' });
        return false;
      }
      this.bankCardDraft = null;
      this.bankDetailsNeedRefresh = true;
      this.toast.show({ type: 'success', message: 'Payment details saved successfully.' });
      // Return to the preserved Cash withdrawal form after saving. The form
      // refreshes its cards and selects the first saved payment detail.
      void this.returnToCashWithdrawal();
      return true;
    } catch (error) {
      console.error('Failed to save payment details', error);
      this.toast.show({ type: 'fail', message: 'Unable to save payment details.' });
      return false;
    }
  }


  /**
   * The wallet dialog is rebuilt when its type changes. Update the URL first
   * so the rebuilt DepositWithdraw component reads the Cash tab, not the tab
   * that was active before entering payment-details management.
   */
  private async returnToCashWithdrawal(): Promise<void> {
    await this.router.navigate([], {
      queryParams: { tab: 'withdraw', subTab: 'cash', dialog: 'wallet' },
      queryParamsHandling: 'merge',
    });
    this.setWalletDialogType(WalletDialogType.DEPOSIT_WITHDRAW);
  }
  /**
   * 刪除地址
   *
   * @param key
   */
  async deleteAddress(key: string) {
    const params = {
      ids: this.addressId ? [Number(this.addressId)] : [],
      key: key,
    };
    //TODO: 需要確認是否需要確認彈窗
    // const confirmData: StandardPopupData = {
    //   type: 'warn',
    //   content:
    //     params.ids.length > 1
    //       ? this.localeService.getValue('del_addres00')
    //       : this.localeService.getValue('del_addres01'),
    //   description: this.localeService.getValue('oper_can_be00'),
    // };

    const res = await firstValueFrom(this.tokenAddressApi.deleteAddress(params));
    if (res?.success && res?.data) {
      this.toast.show({ message: 'Address deleted successfully', type: 'success' });
      this.setWalletDialogType(WalletDialogType.SAVED_ADDRESS);
    } else {
      this.toast.show({ message: this.localeService.getValue('dele_add_f'), type: 'fail' });
    }
  }

  /**
   * 獲取地址列表
   *
   * @param params
   * @returns
   */
  async loadAddressList(params: GetAddParam) {
    const result = await firstValueFrom(this.tokenAddressApi.getTokenadd(params));
    if (result?.success) return result.data;
    return [];
  }

  /**
   * 簡略顯示虛擬貨幣地址，只顯示前五位和後五位，中間用5個點表示
   *
   * @param address 完整地址字串
   * @returns 簡略後的地址
   */
  shortenAddress(address: string): string {
    if (!address || address.length <= 10) return address;
    return `${address.slice(0, 5)}.....${address.slice(-5)}`;
  }

  /**
   * 簡略顯示ID交易樣式，只顯示前六位和後六位，中間用5個點表示
   * @param txId 完整ID
   * @returns 簡略後的ID
   */
  shortenTxId(txId: string): string {
    if (!txId || txId.length <= 10) return txId;
    return `${txId.slice(0, 6)}.....${txId.slice(-6)}`;
  }

  handleMiddleEllipsis(txt: string, front = 5, back = 5, dots = 3): string {
    if (!txt) return txt;
    const ellipsis = '.'.repeat(dots);

    if (txt.length <= front + back + dots) return txt;
    return txt.slice(0, front) + ellipsis + txt.slice(-back);
  }

  /**
   * 檢查是否已有入金紀錄，回傳 true 表示已有紀錄
   */
  async ensureDepositStatus(forceRefresh = false): Promise<boolean> {
    if (!forceRefresh && this.hasDepositRecord !== null) {
      return this.hasDepositRecord;
    }

    if (!forceRefresh && this.depositCheckTask) {
      return this.depositCheckTask;
    }

    this.depositCheckTask = firstValueFrom(this.resourceApi.getUserDeposit())
      .then(hasDeposit => {
        this.hasDepositRecord = hasDeposit;
        return hasDeposit;
      })
      .finally(() => {
        this.depositCheckTask = null;
      });

    return this.depositCheckTask;
  }

  recordDepositStatusBeforeInitiate(status: boolean): void {
    this.localStorageService.depositStatusBeforeInitiate = status;
  }

  getDepositStatusBeforeInitiate(): boolean | null {
    return this.localStorageService.depositStatusBeforeInitiate;
  }

  /**
   * 只在發起存款時推送 initiate-deposit
   *
   * @param params
   * @param params.type
   * @param params.currency
   * @param params.amount
   */
  async pushInitiateDepositOnce(params: { type: string; currency?: string; amount?: string | number }) {
    const hasDeposit = await this.ensureDepositStatus();
    this.recordDepositStatusBeforeInitiate(hasDeposit);

    const payload = this.dataCollectionService.buildDepositPayload(params);
    if (!payload) return;
    this.dataCollectionService.gtmPush('initiate-deposit', payload);
  }

  /**
   *  根據上次存款方式打開存款彈窗
   *
   * @param targetCurrency
   * @param bonusId
   */
  openDepositModalByLastDepositMode(targetCurrency?: string, bonusId?: string) {
    const curDepositMode = this.appService.userInfo$.getValue()?.depositMode || '';
    switch (curDepositMode) {
      case DepositMode.Web3:
        this.openWalletDialogByUrl('wallet', 'deposit', 'web3Wallet', targetCurrency, bonusId);
        break;

      case DepositMode.Transfer:
        this.openWalletDialogByUrl('wallet', 'deposit', 'transfer', targetCurrency, bonusId);
        break;

      case DepositMode.BuyCrypto:
        this.openWalletDialogByUrl('wallet', 'deposit', 'buyCrypto', targetCurrency, bonusId);
        break;

      default:
        this.openWalletDialogByUrl('wallet', '', '', targetCurrency, bonusId);
        break;
    }
  }

  openCashDepositModal(targetCurrency?: string, bonusId?: string) {
    this.openWalletDialogByUrl('wallet', 'deposit', 'cash', targetCurrency, bonusId);
  }

  openCashWithdrawModal(targetCurrency?: string) {
    this.openWalletDialogByUrl('wallet', 'withdraw', 'cash', targetCurrency);
  }
}
